import React, { useCallback, useMemo, useEffect, useState, useRef } from 'react';
import { Grid, Icon, Message } from 'semantic-ui-react';
// Removed context imports - using component state instead
import { useStableVariationList } from '../../hooks/useStableHooks';
import TimedLoader from '../../../utils/TimedLoader';
import './variationList.css';

// Custom comparison function for VariationRow
const arePropsEqual = (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.variation.uuid === nextProps.variation.uuid &&
    prevProps.variation.name === nextProps.variation.name &&
    prevProps.variation.version === nextProps.variation.version &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.characterId === nextProps.characterId &&
    prevProps.isDisabled === nextProps.isDisabled &&
    prevProps.versionCount === nextProps.versionCount // Add versionCount comparison
  );
};

const truncateAfterLastUnderscore = (name) => {
  const lastUnderscoreIndex = name.lastIndexOf('_');
  return lastUnderscoreIndex !== -1 ? name.substring(0, lastUnderscoreIndex) : name;
};

// Simplify the VariationRow component
const VariationRow = React.memo(({
  variation,
  versionCount,
  characterId,
  isSelected,
  onSelect,
  onDelete
}) => {
  console.log('[VARIATION]', variation);
  if (versionCount === 0) return null;
  return (
    <Grid.Row
      className={`variation-list-item ${isSelected ? 'selected' : ''}`}
      onClick={() => onSelect(variation)}
      style={{ cursor: 'pointer' }}
    >
      <Grid.Column width={10}>{truncateAfterLastUnderscore(variation.name)}</Grid.Column>
      <Grid.Column textAlign='center' width={3}>{versionCount}</Grid.Column>
      <Grid.Column width={3} textAlign="right">
        <Icon
          name="trash alternate"
          color="red"
          className="trash-icon"
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(variation);
          }}
        />
      </Grid.Column>
    </Grid.Row>
  );
}, arePropsEqual);


/**
 * Enhanced Variation List component for the bookWorksheet module
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onVariationSelect - Function to handle variation selection
 * @param {Function} props.onDeleteVariation - Function to handle variation deletion
 * @param {Object} props.onVariationListRefreshRef - Ref for parent component to trigger refresh
 * @returns {JSX.Element} VariationsListEnh component
 */
const VariationsListEnh = ({
  onVariationSelect,
  onDeleteVariation,
  onVariationListRefreshRef
}) => {
  // 🚀 ISOLATED: Use stable hook to prevent unnecessary re-renders from other components
  const {
    selectedCharacter,
    selectedVariation,
    selectedLitGroup,
    selectedBook,
    variations,
    variationCounts,
    fetchVariations,
    fetchVariationCounts
  } = useStableVariationList();

  // Extract derived values
  const characterId = selectedCharacter?.uuid;
  const variationId = selectedVariation?.uuid;
  const bookId = selectedBook?.uuid;
  const litGroupId = selectedLitGroup?.uuid;

  // Filter variations for the current character - wrapped in useMemo to fix ESLint warning
  const characterVariations = useMemo(() => {
    return characterId && variations ?
      variations.filter(variation => variation.characterId === characterId) : [];
  }, [characterId, variations]);

  // Add render tracking to prove optimization is working
  const renderCount = useRef(0);
  renderCount.current++;

  // if (process.env.NODE_ENV === 'development' && window.DEBUG_RENDERS) {
  //   console.log(`[RENDER] VariationList render #${renderCount.current} for character: ${characterId}`);
  // }

  // Phase 3: Replace context with local component state
  // const [isLoading] = useState(false);

  // Keep some local state for UI-specific concerns
  const [localSelectedId, setLocalSelectedId] = useState(variationId);
  const [errorMessage, setErrorMessage] = useState('');

  // Function to force refresh the variations list
  const refreshVariations = useCallback(() => {
    // Conditional logging for performance
    if (process.env.NODE_ENV === 'development' && window.DEBUG_VARIATION_LIST) {
      console.log('VariationList: refreshVariations called - REFRESH TRIGGERED');
    }

    // Refresh both variations and variation counts when called
    if (characterId) {
      if (process.env.NODE_ENV === 'development' && window.DEBUG_VARIATION_LIST) {
        console.log('VariationList: Refreshing variations and counts for character', characterId);
      }
      console.log(`[SELECTED LIT & BOOK] Refresh ${litGroupId}, ${bookId}`);
      fetchVariations({ litGroupId, bookId, characterId, forceRefresh: true });
      fetchVariationCounts({ litGroupId, bookId, characterId, forceRefresh: true });
    }
  }, [characterId, litGroupId, bookId, fetchVariations, fetchVariationCounts]);


  // Expose the refresh function to the parent component via ref
  useEffect(() => {
    if (onVariationListRefreshRef && typeof onVariationListRefreshRef === 'object' && 'current' in onVariationListRefreshRef) {
      onVariationListRefreshRef.current = refreshVariations;
    }
  }, [refreshVariations, onVariationListRefreshRef]);

  // Update local selected ID when prop changes
  useEffect(() => {
    if (variationId !== localSelectedId) {
      console.log('[VariationList] Updating localSelectedId from', localSelectedId, 'to', variationId);
      setLocalSelectedId(variationId);
    }
  }, [variationId, localSelectedId]);

  // Additional effect to ensure selection is maintained after data refresh
  useEffect(() => {
    // If we have a variationId but no localSelectedId, set it
    if (variationId && !localSelectedId) {
      console.log('[VariationList] Setting localSelectedId to maintain selection:', variationId);
      setLocalSelectedId(variationId);
    }
  }, [variationId, localSelectedId, characterVariations]);

  useEffect(() => {
    console.log('🎯 [VARIATION_LIST] useEffect triggered - characterId:', characterId, 'litGroupId:', litGroupId, 'bookId:', bookId);

    if (characterId) {
      console.log('🎯 [VARIATION_LIST] Fetching variations for character:', characterId);
      console.log(`🎯 [VARIATION_LIST] Context: litGroupId=${litGroupId}, bookId=${bookId}`);

      fetchVariations({ litGroupId, bookId, characterId });
      fetchVariationCounts({ litGroupId, bookId, characterId });
    } else {
      console.log('🎯 [VARIATION_LIST] No characterId provided, skipping fetch');
    }
  }, [characterId, litGroupId, bookId, fetchVariations, fetchVariationCounts]);


  // Reset component when characterId changes
  useEffect(() => {
    // Clear local state when character changes
    setErrorMessage('');
  }, [characterId]);

  // Enhanced handler for variation selection with error handling
  // Update the handleSelect function to be simpler
  const handleSelect = useCallback((variation) => {
    // If this variation is already selected, don't do anything
    if (localSelectedId === variation.uuid) {
      return;
    }

    // Clear any previous errors
    setErrorMessage('');

    // Update local selection state immediately for responsive UI
    setLocalSelectedId(variation.uuid);

    // Call the parent's onVariationSelect function
    onVariationSelect(variation)
      .catch(error => {
        console.error('Error selecting variation:', error);
        setErrorMessage(`Error loading variation: ${error.message || 'Unknown error'}`);
      });
  }, [
    onVariationSelect,
    localSelectedId
  ]);

  // Memoize the sorted variations array
  const sortedVariations = useMemo(() => {
    if (!Array.isArray(characterVariations)) return [];

    // Conditional logging for performance
    if (process.env.NODE_ENV === 'development' && window.DEBUG_VARIATION_LIST) {
      console.log(`[VariationList] Variations for character ${characterId}:`, characterVariations);
    }

    // Check for duplicates
    const uuids = new Set();
    const uniqueVars = [];
    characterVariations.forEach(v => {
      // ✅ FIX: Add null check before accessing uuid
      if (v && v.uuid && !uuids.has(v.uuid)) {
        uuids.add(v.uuid);
        uniqueVars.push(v);
      } else {
        if (process.env.NODE_ENV === 'development' && window.DEBUG_VARIATION_LIST) {
          console.warn(`[VariationList] Duplicate variation found with UUID ${v?.uuid}`);
        }
      }
    });

    if (process.env.NODE_ENV === 'development' && window.DEBUG_VARIATION_LIST) {
      console.log(`[VariationList] Original variations count: ${characterVariations.length}, Unique variations count: ${uniqueVars.length}`);
    }

    return uniqueVars.sort((a, b) => a.name.localeCompare(b.name));
  }, [characterVariations, characterId]);

  // Clear error message after a timeout
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage('');
      }, 5000); // Clear after 5 seconds

      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  // Show loading state if fetching
  // const dataLoading = isLoading && sortedVariations.length === 0;

  return (
    <div style={{ position: 'relative' }}> {/* Add position relative to contain the overlay */}
      {/* Error message display */}
      {errorMessage && (
        <Message
          negative
          style={{ marginTop: '10px', marginBottom: '10px' }}
        >
          <Message.Header>Error</Message.Header>
          <p>{errorMessage}</p>
        </Message>
      )}

      <div style={{ marginTop: '10px' }}>
        {/* Header row */}
        <div className="variation-list-container">
          <Grid className='listGrid'>
            <Grid.Row style={{ fontWeight: 'bold', borderBottom: '1px solid #ddd', padding: "8px 8px 8px 10px" }}>
              <Grid.Column width={8}>Name</Grid.Column>
              <Grid.Column width={3}>Variations</Grid.Column>
              <Grid.Column width={5}></Grid.Column>
            </Grid.Row>

            {/* Loading indicator with timeout */}
            {sortedVariations.length === 0 ? (
              <Grid.Row style={{ padding: '20px 0', textAlign: 'center' }}>
                <Grid.Column width={16}>
                  <TimedLoader
                    active
                    inline="centered"
                    size="small"
                    initialText="Loading Variations"
                    timeout={2000}
                    afterTimeout={
                      <p style={{ color: '#888' }}>
                        No variations found.<br />
                        Create a new variation to get started.
                      </p>
                    }
                  />
                </Grid.Column>
              </Grid.Row>
            ) : (
              sortedVariations.map((variation, index) => (
                <VariationRow
                  key={`${variation.uuid}-${index}`}
                  variation={variation}
                  characterId={characterId}
                  isSelected={localSelectedId === variation.uuid}
                  onSelect={handleSelect}
                  onDelete={onDeleteVariation}
                  versionCount={variationCounts[variation.uuid] || 0}
                />
              ))
            )}
          </Grid>
        </div>
      </div>
    </div>
  );
};

// Add display name for debugging
VariationsListEnh.displayName = 'VariationsListEnh';

// Memoize component to prevent unnecessary re-renders during text editing
const areVariationListPropsEqual = (prevProps, nextProps) => {
  // Compare function props by reference (should be stable with useCallback)
  return (
    prevProps.onVariationSelect === nextProps.onVariationSelect &&
    prevProps.onDeleteVariation === nextProps.onDeleteVariation &&
    prevProps.onVariationListRefreshRef === nextProps.onVariationListRefreshRef
  );
};

export default React.memo(VariationsListEnh, areVariationListPropsEqual);
